import React, { useState, useEffect, useCallback } from 'react';
import DatePicker from 'react-datepicker';
import { fetchAuth } from '../../utils/api';
import ConfirmationModal from '../common/ConfirmationModal';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000';

const PLAN_DURATIONS = { Free: 0, Basic: 3, Standard: 6, Premium: 12 };

const EXTRA_USER_OPTIONS = [
    { value: 0, label: 'No Extra Users (1 user included)', price: '—' },
    { value: 2, label: '2 Extra Users (3 total)', price: '₹499/mo' },
    { value: 4, label: '4 Extra Users (5 total)', price: '₹699/mo' },
    { value: 6, label: '6 Extra Users (7 total)', price: '₹899/mo' },
    { value: 8, label: '8 Extra Users (9 total)', price: '₹1,099/mo' },
    { value: 10, label: '10 Extra Users (11 total)', price: '₹1,299/mo' },
];

// Helper: get a consistent ID from a company object
const getId = (c) => c._id || c.id;



function SuperAdminView({ user, handleApiError, showNotification }) {
    const [companies, setCompanies] = useState([]);
    const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
    const [selectedCompany, setSelectedCompany] = useState(null);
    const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
    const [companyToDelete, setCompanyToDelete] = useState(null);

    const fetchCompanies = useCallback(async () => {
        try {
            const res = await fetchAuth(`${API_URL}/api/superadmin/companies`);
            if (!res.ok) throw new Error('Could not fetch companies');
            setCompanies(await res.json());
        } catch (err) { handleApiError(err); }
    }, [handleApiError]);

    useEffect(() => { fetchCompanies(); }, [fetchCompanies]);

    const handleDeleteCompany = async () => {
        if (!companyToDelete) return;
        try {
            await fetchAuth(`${API_URL}/api/superadmin/companies/${getId(companyToDelete)}`, { method: 'DELETE' });
            showNotification('Company deleted successfully.', 'success');
            fetchCompanies();
        } catch (err) { handleApiError(err); }
        finally { setIsConfirmModalOpen(false); setCompanyToDelete(null); }
    };

    const openConfirmModal = (company) => { setCompanyToDelete(company); setIsConfirmModalOpen(true); };
    const openDetailModal = (company) => { setSelectedCompany(company); setIsDetailModalOpen(true); };

    const handleStatusToggle = async (companyId, currentStatus) => {
        try {
            await fetchAuth(`${API_URL}/api/superadmin/companies/${companyId}/status`, {
                method: 'PUT', body: JSON.stringify({ isActive: !currentStatus })
            });
            showNotification('Company status updated!', 'success');
            fetchCompanies();
        } catch (err) { handleApiError(err); }
    };

    // ── Company Detail Modal ────────────────────────────────
    const CompanyDetailModal = ({ company, onClose, onUpdate }) => {
        const companyId = getId(company);
        let initialAddons = {};
        try {
            initialAddons = typeof company.addons === 'string' ? JSON.parse(company.addons) : (company.addons || {});
        } catch (e) { initialAddons = {}; }

        const [activeTab, setActiveTab] = useState('overview');
        const [summary, setSummary] = useState(null);
        const [plan, setPlan] = useState(company.subscriptionPlan || company.subscription_plan || 'Free');
        const [endDate, setEndDate] = useState(company.planEndDate || company.plan_end_date ? new Date(company.planEndDate || company.plan_end_date) : null);
        const [addons, setAddons] = useState(initialAddons);
        const [saving, setSaving] = useState(false);

        useEffect(() => {
            if (!companyId) return;
            const fetchSummary = async () => {
                try {
                    const res = await fetchAuth(`${API_URL}/api/superadmin/companies/${companyId}/summary`);
                    if (res.ok) setSummary(await res.json());
                } catch (err) { console.error("Failed to fetch company summary", err); }
            };
            fetchSummary();
        }, [companyId]);

        const handlePlanChange = (newPlan) => {
            setPlan(newPlan);
            const months = PLAN_DURATIONS[newPlan];
            if (months > 0) {
                const d = new Date();
                d.setMonth(d.getMonth() + months);
                setEndDate(d);
            } else {
                setEndDate(null);
            }
        };

        const handleUpdatePlan = async () => {
            try {
                await fetchAuth(`${API_URL}/api/superadmin/companies/${companyId}/plan`, {
                    method: 'PUT', body: JSON.stringify({ subscriptionPlan: plan, planEndDate: endDate })
                });
                showNotification('Plan updated!', 'success');
                onUpdate(); onClose();
            } catch (err) { handleApiError(err); }
        };

        const handleSaveAddons = async () => {
            setSaving(true);
            try {
                const res = await fetchAuth(`${API_URL}/api/superadmin/companies/${companyId}/addons`, {
                    method: 'PUT', body: JSON.stringify({ addons })
                });
                if (res.ok) {
                    showNotification('Add-ons updated!', 'success');
                    onUpdate();
                } else {
                    const errData = await res.json();
                    showNotification(errData.message, 'error');
                }
            } catch (err) { handleApiError(err); }
            finally { setSaving(false); }
        };

        const handleRenewPlan = async (duration) => {
            try {
                const res = await fetchAuth(`${API_URL}/api/superadmin/companies/${companyId}/renew`, {
                    method: 'POST', body: JSON.stringify({ duration })
                });
                if (res.ok) {
                    const data = await res.json();
                    setEndDate(new Date(data.newEndDate));
                    showNotification('Plan renewed & account reactivated!', 'success');
                    onUpdate();
                } else {
                    const errData = await res.json();
                    showNotification(errData.message, 'error');
                }
            } catch (err) { handleApiError(err); }
        };

        const handleSendEmail = async () => {
            try {
                await fetchAuth(`${API_URL}/api/superadmin/companies/${companyId}/send-payment-email`, { method: 'POST' });
                showNotification('Payment email sent!', 'success');
            } catch (err) { handleApiError(err); }
        };

        // Tab style — avoid mixing shorthand `border` with `borderBottom`
        const tabStyle = (tab) => ({
            padding: '10px 20px',
            cursor: 'pointer',
            borderTop: 'none',
            borderLeft: 'none',
            borderRight: 'none',
            borderBottom: activeTab === tab ? '3px solid #6366f1' : '3px solid transparent',
            fontWeight: activeTab === tab ? '700' : '500',
            color: activeTab === tab ? '#6366f1' : '#64748b',
            background: 'none',
            fontSize: '14px',
            transition: 'all 0.2s'
        });

        const cardStyle = {
            background: 'linear-gradient(135deg, #f8fafc, #eef2ff)', borderRadius: '12px',
            padding: '16px 20px', textAlign: 'center', border: '1px solid #e2e8f0'
        };
        const sectionTitle = { fontSize: '15px', fontWeight: '700', color: '#1e293b', margin: '16px 0 10px' };
        const badgeStyle = (bg) => ({
            display: 'inline-block', padding: '3px 10px', borderRadius: '12px', fontSize: '11px',
            fontWeight: '600', backgroundColor: bg, color: '#fff', marginRight: '4px'
        });
        const formatDate = (d) => d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';

        return (
            <div className="modal-overlay">
                <div className="modal-content wide-modal" style={{ maxWidth: '720px', maxHeight: '85vh', overflowY: 'auto', borderRadius: '16px' }}>
                    {/* Header */}
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
                        <h3 style={{ margin: 0, fontSize: '20px' }}>🏢 {company.name}</h3>
                        <span style={badgeStyle(company.isActive ? '#22c55e' : '#ef4444')}>{company.isActive ? 'Active' : 'Suspended'}</span>
                    </div>
                    <p style={{ color: '#64748b', fontSize: '13px', margin: '0 0 12px' }}>{company.ownerEmail} · {company.contactNumber || '—'}</p>

                    {/* Tabs */}
                    <div style={{ display: 'flex', borderBottom: '1px solid #e2e8f0', marginBottom: '16px', overflowX: 'auto' }}>
                        <button style={tabStyle('overview')} onClick={() => setActiveTab('overview')}>📊 Overview</button>
                        <button style={tabStyle('plan')} onClick={() => setActiveTab('plan')}>💳 Plan & Add-Ons</button>
                        <button style={tabStyle('modules')} onClick={() => setActiveTab('modules')}>📦 Company Data</button>
                        <button style={tabStyle('history')} onClick={() => setActiveTab('history')}>📜 Plan History</button>
                    </div>

                    {/* ── Tab: Overview ─────────────────────── */}
                    {activeTab === 'overview' && (
                        <div>
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '12px', marginBottom: '16px' }}>
                                {[
                                    { label: 'Revenue', value: summary ? `₹${Number(summary.totalRevenue).toLocaleString('en-IN')}` : '...', icon: '💰', k: 'revenue' },
                                    { label: 'Purchase Cost', value: summary ? `₹${Number(summary.totalPurchaseCost).toLocaleString('en-IN')}` : '...', icon: '📥', k: 'cost' },
                                    { label: 'Profit', value: summary ? `₹${Number(summary.totalProfit).toLocaleString('en-IN')}` : '...', icon: '📈', k: 'profit' },
                                    { label: 'Orders', value: summary?.totalOrders ?? '...', icon: '📋', k: 'orders' },
                                    { label: 'Leads', value: summary?.totalLeads ?? '...', icon: '👤', k: 'leads' },
                                    { label: 'Customers', value: summary?.totalCustomers ?? '...', icon: '🤝', k: 'customers' },
                                    { label: 'Products', value: summary?.totalProducts ?? '...', icon: '📦', k: 'products' },
                                    { label: 'Users', value: summary?.users?.length ?? '...', icon: '👥', k: 'users' },
                                ].map(c => (
                                    <div key={c.k} style={cardStyle}>
                                        <div style={{ fontSize: '20px' }}>{c.icon}</div>
                                        <div style={{ fontSize: '20px', fontWeight: '800', color: c.k === 'profit' ? (summary?.totalProfit >= 0 ? '#16a34a' : '#ef4444') : '#1e293b' }}>{c.value}</div>
                                        <div style={{ fontSize: '12px', color: '#64748b' }}>{c.label}</div>
                                    </div>
                                ))}
                            </div>

                            {/* Users List */}
                            <p style={sectionTitle}>👥 Registered Users ({summary?.users?.length || 0})</p>
                            <div style={{ borderRadius: '10px', border: '1px solid #e2e8f0', overflow: 'hidden' }}>
                                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                                    <thead><tr style={{ background: '#f1f5f9' }}>
                                        <th style={{ padding: '8px 12px', textAlign: 'left' }}>Username</th>
                                        <th style={{ padding: '8px 12px', textAlign: 'left' }}>Role</th>
                                        <th style={{ padding: '8px 12px', textAlign: 'left' }}>Phone</th>
                                    </tr></thead>
                                    <tbody>
                                        {summary?.users?.length > 0 ? summary.users.map(u => (
                                            <tr key={u.id} style={{ borderTop: '1px solid #e2e8f0' }}>
                                                <td style={{ padding: '8px 12px', fontWeight: '600' }}>{u.username}</td>
                                                <td style={{ padding: '8px 12px' }}><span style={badgeStyle('#6366f1')}>{u.role}</span></td>
                                                <td style={{ padding: '8px 12px', color: '#64748b' }}>{u.phone || '—'}</td>
                                            </tr>
                                        )) : <tr><td colSpan={3} style={{ padding: '16px', textAlign: 'center', color: '#94a3b8' }}>No users found</td></tr>}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}

                    {/* ── Tab: Plan & Add-Ons ──────────────── */}
                    {activeTab === 'plan' && (
                        <div>
                            <p style={sectionTitle}>📋 Subscription Plan</p>
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '16px' }}>
                                <div className="input-group">
                                    <label>Plan</label>
                                    <select value={plan} onChange={(e) => handlePlanChange(e.target.value)} style={{ width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid #cbd5e1' }}>
                                        <option>Free</option><option>Basic</option><option>Standard</option><option>Premium</option>
                                    </select>
                                    <small style={{ color: '#64748b' }}>
                                        {plan === 'Free' ? 'No expiry' : `${PLAN_DURATIONS[plan]} months from today`}
                                    </small>
                                </div>
                                <div className="input-group">
                                    <label>Plan End Date</label>
                                    <DatePicker selected={endDate} onChange={(date) => setEndDate(date)} className="customer-input" dateFormat="dd/MM/yyyy" />
                                    <small style={{ color: '#64748b' }}>
                                        {endDate ? `Expires: ${formatDate(endDate)}` : 'No end date'}
                                    </small>
                                </div>
                            </div>
                            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '20px' }}>
                                <button onClick={handleUpdatePlan} className="confirm-btn" style={{ borderRadius: '8px' }}>💾 Save Plan</button>
                                <button onClick={() => handleRenewPlan('3_months')} style={{ padding: '8px 14px', borderRadius: '8px', background: '#4CAF50', color: '#fff', border: 'none', cursor: 'pointer', fontWeight: '600' }}>+3 Months</button>
                                <button onClick={() => handleRenewPlan('6_months')} style={{ padding: '8px 14px', borderRadius: '8px', background: '#2196F3', color: '#fff', border: 'none', cursor: 'pointer', fontWeight: '600' }}>+6 Months</button>
                                <button onClick={() => handleRenewPlan('1_year')} style={{ padding: '8px 14px', borderRadius: '8px', background: '#9C27B0', color: '#fff', border: 'none', cursor: 'pointer', fontWeight: '600' }}>+1 Year</button>
                                <button onClick={handleSendEmail} className="btn-secondary" style={{ borderRadius: '8px' }}>📧 Send Payment Email</button>
                            </div>

                            <hr style={{ border: 'none', borderTop: '1px solid #e2e8f0', margin: '16px 0' }} />

                            <p style={sectionTitle}>🧩 Module Add-Ons</p>
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '16px' }}>
                                <label style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '14px', borderRadius: '10px', border: (addons.profit_report || addons.profitReport) ? '2px solid #6366f1' : '1px solid #e2e8f0', cursor: 'pointer', background: (addons.profit_report || addons.profitReport) ? '#eef2ff' : '#fff' }}>
                                    <input type="checkbox" checked={!!(addons.profit_report || addons.profitReport)} onChange={(e) => setAddons({ ...addons, profit_report: e.target.checked })} />
                                    <div>
                                        <div style={{ fontWeight: '600', fontSize: '14px' }}>📊 Profit Report</div>
                                        <div style={{ fontSize: '12px', color: '#64748b' }}>₹799/month</div>
                                    </div>
                                </label>
                                <label style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '14px', borderRadius: '10px', border: (addons.closing_margin || addons.closingMargin) ? '2px solid #6366f1' : '1px solid #e2e8f0', cursor: 'pointer', background: (addons.closing_margin || addons.closingMargin) ? '#eef2ff' : '#fff' }}>
                                    <input type="checkbox" checked={!!(addons.closing_margin || addons.closingMargin)} onChange={(e) => setAddons({ ...addons, closing_margin: e.target.checked })} />
                                    <div>
                                        <div style={{ fontWeight: '600', fontSize: '14px' }}>📈 Closing Margin</div>
                                        <div style={{ fontSize: '12px', color: '#64748b' }}>₹899/month</div>
                                    </div>
                                </label>
                            </div>

                            <p style={sectionTitle}>👥 Extra User Seats</p>
                            <select value={addons.extra_users || addons.extraUsers || 0} onChange={(e) => setAddons({ ...addons, extra_users: parseInt(e.target.value) })} style={{ width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid #cbd5e1', marginBottom: '12px' }}>
                                {EXTRA_USER_OPTIONS.map(opt => (
                                    <option key={opt.value} value={opt.value}>{opt.label} — {opt.price}</option>
                                ))}
                            </select>

                            <button onClick={handleSaveAddons} disabled={saving} className="confirm-btn" style={{ borderRadius: '8px', width: '100%' }}>
                                {saving ? 'Saving...' : '💾 Save Add-Ons'}
                            </button>
                        </div>
                    )}

                    {/* ── Tab: Company Data ─────────────────── */}
                    {activeTab === 'modules' && (
                        <div>
                            <p style={sectionTitle}>🧾 Recent Sales</p>
                            <div style={{ borderRadius: '10px', border: '1px solid #e2e8f0', overflow: 'hidden', marginBottom: '16px' }}>
                                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                                    <thead><tr style={{ background: '#f1f5f9' }}>
                                        <th style={{ padding: '8px 12px', textAlign: 'left' }}>Invoice</th>
                                        <th style={{ padding: '8px 12px', textAlign: 'left' }}>Customer</th>
                                        <th style={{ padding: '8px 12px', textAlign: 'right' }}>Amount</th>
                                        <th style={{ padding: '8px 12px', textAlign: 'left' }}>Date</th>
                                    </tr></thead>
                                    <tbody>
                                        {summary?.recentSales?.length > 0 ? summary.recentSales.map(s => (
                                            <tr key={s.id} style={{ borderTop: '1px solid #e2e8f0' }}>
                                                <td style={{ padding: '8px 12px', fontWeight: '600' }}>{s.invoice_number || '—'}</td>
                                                <td style={{ padding: '8px 12px' }}>{s.customer_name || 'Walk-in'}</td>
                                                <td style={{ padding: '8px 12px', textAlign: 'right', fontWeight: '600' }}>₹{Number(s.total_amount).toLocaleString('en-IN')}</td>
                                                <td style={{ padding: '8px 12px', color: '#64748b' }}>{formatDate(s.sale_date)}</td>
                                            </tr>
                                        )) : <tr><td colSpan={4} style={{ padding: '16px', textAlign: 'center', color: '#94a3b8' }}>No sales yet</td></tr>}
                                    </tbody>
                                </table>
                            </div>

                            <p style={sectionTitle}>📥 Recent Purchases</p>
                            <div style={{ borderRadius: '10px', border: '1px solid #e2e8f0', overflow: 'hidden', marginBottom: '16px' }}>
                                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                                    <thead><tr style={{ background: '#f1f5f9' }}>
                                        <th style={{ padding: '8px 12px', textAlign: 'left' }}>Bill #</th>
                                        <th style={{ padding: '8px 12px', textAlign: 'left' }}>Supplier</th>
                                        <th style={{ padding: '8px 12px', textAlign: 'right' }}>Amount</th>
                                        <th style={{ padding: '8px 12px', textAlign: 'left' }}>Date</th>
                                    </tr></thead>
                                    <tbody>
                                        {summary?.recentPurchases?.length > 0 ? summary.recentPurchases.map(p => {
                                            const pbDate = p.purchase_date || p.purchaseDate || p.created_at || p.createdAt;
                                            return (
                                                <tr key={p.id} style={{ borderTop: '1px solid #e2e8f0' }}>
                                                    <td style={{ padding: '8px 12px', fontWeight: '600' }}>{p.bill_number || p.billNumber || '—'}</td>
                                                    <td style={{ padding: '8px 12px' }}>{p.supplier_name || p.supplierName || '—'}</td>
                                                    <td style={{ padding: '8px 12px', textAlign: 'right', fontWeight: '600' }}>₹{(Number(p.total_amount || p.totalAmount) || 0).toLocaleString('en-IN')}</td>
                                                    <td style={{ padding: '8px 12px', color: '#64748b' }}>{pbDate ? formatDate(pbDate) : '—'}</td>
                                                </tr>
                                            )
                                        }) : <tr><td colSpan={4} style={{ padding: '16px', textAlign: 'center', color: '#94a3b8' }}>No purchases yet</td></tr>}
                                    </tbody>
                                </table>
                            </div>

                            <p style={sectionTitle}>📊 Active Add-Ons</p>
                            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                                {(addons.profit_report || addons.profitReport) && <span style={badgeStyle('#10b981')}>Profit Report</span>}
                                {(addons.closing_margin || addons.closingMargin) && <span style={badgeStyle('#3b82f6')}>Closing Margin</span>}
                                {(addons.extra_users > 0 || addons.extraUsers > 0) && <span style={badgeStyle('#8b5cf6')}>+{addons.extra_users || addons.extraUsers} Users</span>}
                                {!(addons.profit_report || addons.profitReport) && !(addons.closing_margin || addons.closingMargin) && !(addons.extra_users > 0 || addons.extraUsers > 0) &&
                                    <span style={{ color: '#94a3b8', fontSize: '13px' }}>No active add-ons</span>
                                }
                            </div>
                        </div>
                    )}

                    {/* ── Tab: Plan History ──────────────── */}
                    {activeTab === 'history' && (
                        <div>
                            <p style={sectionTitle}>📜 Subscription & Plan Purchase History</p>
                            {summary?.planHistory?.length > 0 ? (
                                <div style={{ borderRadius: '10px', border: '1px solid #e2e8f0', overflow: 'hidden' }}>
                                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                                        <thead><tr style={{ background: '#f1f5f9' }}>
                                            <th style={{ padding: '8px 12px', textAlign: 'left' }}>Date</th>
                                            <th style={{ padding: '8px 12px', textAlign: 'left' }}>Action</th>
                                            <th style={{ padding: '8px 12px', textAlign: 'left' }}>Plan</th>
                                            <th style={{ padding: '8px 12px', textAlign: 'right' }}>Amount</th>
                                            <th style={{ padding: '8px 12px', textAlign: 'left' }}>Details</th>
                                        </tr></thead>
                                        <tbody>
                                            {summary.planHistory.map((h, idx) => {
                                                const actionColors = {
                                                    plan_updated: '#3b82f6', renewed: '#22c55e', addons_updated: '#8b5cf6',
                                                    invoice_sent: '#f59e0b', created: '#6366f1'
                                                };
                                                const actionLabels = {
                                                    plan_updated: 'Plan Changed', renewed: 'Renewed', addons_updated: 'Add-Ons Updated',
                                                    invoice_sent: 'Invoice Sent', created: 'Created'
                                                };
                                                return (
                                                    <tr key={h.id || idx} style={{ borderTop: '1px solid #e2e8f0' }}>
                                                        <td style={{ padding: '8px 12px', color: '#64748b', whiteSpace: 'nowrap' }}>
                                                            {formatDate(h.created_at || h.createdAt || h.plan_start_date || h.planStartDate)}
                                                            <br /><span style={{ fontSize: '11px' }}>{(h.created_at || h.createdAt) ? new Date(h.created_at || h.createdAt).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' }) : ''}</span>
                                                        </td>
                                                        <td style={{ padding: '8px 12px' }}>
                                                            <span style={{ display: 'inline-block', padding: '3px 10px', borderRadius: '12px', fontSize: '11px', fontWeight: '600', backgroundColor: actionColors[h.action] || '#94a3b8', color: '#fff' }}>
                                                                {actionLabels[h.action] || h.action}
                                                            </span>
                                                        </td>
                                                        <td style={{ padding: '8px 12px', fontWeight: '600' }}>{h.plan_name || h.planName || '—'}</td>
                                                        <td style={{ padding: '8px 12px', textAlign: 'right', fontWeight: 'bold', color: '#0f172a' }}>
                                                            ₹{(Number(h.amount) || 0).toLocaleString('en-IN')}
                                                        </td>
                                                        <td style={{ padding: '8px 12px', color: '#64748b', fontSize: '12px', maxWidth: '180px' }}>{h.notes || '—'}</td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                </div>
                            ) : (
                                <div style={{ textAlign: 'center', padding: '40px 20px', color: '#94a3b8' }}>
                                    <div style={{ fontSize: '40px', marginBottom: '8px' }}>📋</div>
                                    <p style={{ margin: 0 }}>No plan history yet. History will be recorded when plans are updated, renewed, or invoices are sent.</p>
                                </div>
                            )}
                        </div>
                    )}

                    {/* Close */}
                    <div style={{ display: 'flex', justifyContent: 'center', marginTop: '20px' }}>
                        <button type="button" className="cancel-btn" onClick={onClose} style={{ borderRadius: '8px', padding: '10px 30px' }}>Close</button>
                    </div>
                </div>
            </div>
        );
    };

    // ── Status helpers ──────────────────────────────────────
    const getStatusInfo = (c) => {
        let statusText = c.isActive ? 'Active' : 'Suspended';
        let bgColor = c.isActive ? '#22c55e' : '#ef4444';
        if (c.isActive && c.planEndDate) {
            const diffDays = Math.ceil((new Date(c.planEndDate).getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24));
            if (diffDays <= 0 && diffDays >= -15) { statusText = `Grace (${15 + diffDays}d)`; bgColor = '#f59e0b'; }
            else if (diffDays > 0 && diffDays <= 15) { statusText = `Expiring (${diffDays}d)`; bgColor = '#f59e0b'; }
        }
        return { statusText, bgColor };
    };

    const getActiveAddons = (addons) => {
        if (!addons) return [];
        const tags = [];
        if (addons.profit_report) tags.push('Profit');
        if (addons.closing_margin) tags.push('Margin');
        if (addons.extra_users > 0) tags.push(`+${addons.extra_users} Users`);
        return tags;
    };

    // ── Main Render ─────────────────────────────────────────
    return (
        <div className="page-container">
            {isDetailModalOpen && <CompanyDetailModal company={selectedCompany} onClose={() => setIsDetailModalOpen(false)} onUpdate={fetchCompanies} />}
            {isConfirmModalOpen && <ConfirmationModal onCancel={() => setIsConfirmModalOpen(false)} onConfirm={handleDeleteCompany} customerName={companyToDelete?.name} />}

            <div className="page-header"><h2 className="page-title">Software Owner Dashboard</h2></div>
            <div className="data-display">
                <h3>Registered Companies ({companies.length})</h3>
                <div className="inventory-table-container">
                    <table className="inventory-table">
                        <thead>
                            <tr>
                                <th>Company</th>
                                <th>Email</th>
                                <th>Plan</th>
                                <th>Expires</th>
                                <th>Add-Ons</th>
                                <th>Status</th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {companies.map((c, index) => {
                                const cid = getId(c) || index;
                                const { statusText, bgColor } = getStatusInfo(c);
                                const addonTags = getActiveAddons(c.addons);
                                return (
                                    <tr key={cid}>
                                        <td><button className="link-style-btn" onClick={() => openDetailModal(c)}>{c.name}</button></td>
                                        <td>{c.ownerEmail}</td>
                                        <td><span className={`status-badge plan-${(c.subscriptionPlan || 'free').toLowerCase()}`}>{c.subscriptionPlan || 'Free'}</span></td>
                                        <td style={{ fontSize: '13px', color: '#64748b' }}>
                                            {c.planEndDate ? new Date(c.planEndDate).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—'}
                                        </td>
                                        <td>
                                            {addonTags.length > 0
                                                ? addonTags.map(t => <span key={t} style={{ display: 'inline-block', padding: '2px 8px', borderRadius: '10px', fontSize: '11px', fontWeight: '600', backgroundColor: '#eef2ff', color: '#6366f1', marginRight: '4px' }}>{t}</span>)
                                                : <span style={{ color: '#cbd5e1', fontSize: '12px' }}>—</span>
                                            }
                                        </td>
                                        <td><span style={{ display: 'inline-block', padding: '3px 10px', borderRadius: '12px', fontSize: '11px', fontWeight: '600', backgroundColor: bgColor, color: '#fff' }}>{statusText}</span></td>
                                        <td>
                                            <div className="table-actions">
                                                <label className="toggle-switch">
                                                    <input type="checkbox" checked={c.isActive} onChange={() => handleStatusToggle(getId(c), c.isActive)} />
                                                    <span className="slider"></span>
                                                </label>
                                                <button className="icon-btn delete" onClick={() => openConfirmModal(c)}>
                                                    <svg viewBox="0 0 24 24"><path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z"></path></svg>
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
}
export default SuperAdminView;
